'use strict';

const csrf = require('public/js/lib/csrf/utils');
const localStorageSlim = require('localstorage-slim');
const { notifySteerVehicleGarageUpdated } = require('public/js/lib/steer-outbound');
const STORAGE_KEYS = require('public/js/lib/storage-keys');

const RP_GARAGE_STATE_KEY = '__rp_vehicle_picker_garage_state_v1';
const SELECT_VEHICLE_ENDPOINT = '/ajax/vehicle-picker/select';
const SEARCH_ALL_CARS_ENDPOINT = '/ajax/garage/search-all-cars';

function validateGaragePayload(garage) {
	return Array.isArray(garage);
}

/**
 * Garage data
 * @section garage
 */

const createDefaultGarageState = () => {
	// `requestId` is bumped on invalidate-and-refetch so stale in-flight
	// `loadUserGarage` callbacks can detect themselves and bail.
	// `lastGarageFetchOk` tracks whether the most recent get-garage AJAX succeeded
	// so stay-on-page callers can fall back to reload when STEER cannot be notified.
	return {
		garage: null,
		loaded: false,
		loading: false,
		cacheCleared: false,
		requestId: 0,
		lastGarageFetchOk: true,
	};
};

const getGarageState = () => {
	if (typeof globalThis === 'undefined') {
		return createDefaultGarageState();
	}

	if (!globalThis[RP_GARAGE_STATE_KEY]) {
		globalThis[RP_GARAGE_STATE_KEY] = createDefaultGarageState();
	}

	return globalThis[RP_GARAGE_STATE_KEY];
};

function triggerGarageUpdated(options = {}) {
	const notifySteer = options.notifySteer !== false;
	const state = getGarageState();
	if (typeof document === 'undefined' || typeof rp$ !== 'function') {
		return;
	}
	const $doc = rp$(document);
	if (!$doc || typeof $doc.trigger !== 'function') {
		return;
	}
	$doc.trigger('garageUpdated', state.garage);
	if (notifySteer) {
		notifySteerVehicleGarageUpdated(state.garage);
	}
}

function loadGarageFromCache(clearCached = false) {
	const state = getGarageState();

	if (clearCached === true && state.cacheCleared !== true) {
		localStorageSlim.remove(STORAGE_KEYS.GARAGE_V1);
		state.cacheCleared = true;
		return false;
	}

	const storedGarage = localStorageSlim.get(STORAGE_KEYS.GARAGE_V1);
	if (!validateGaragePayload(storedGarage)) {
		return false;
	}
	state.garage = storedGarage;
	state.loaded = true;
	state.loading = false;

	triggerGarageUpdated();
	return true;
}

function loadUserGarage() {
	const state = getGarageState();

	if (state.loading) {
		return;
	}

	state.loading = true;
	// Captured by both callbacks and checked before any mutation/emission
	// so a stale (pre-clear) response can't clobber a fresh fetch.
	const requestId = state.requestId;

	globalThis.$ajax({
		method: 'GET',
		url: '/ajax/vehicle-picker/get-garage',
		dataType: 'json',
		success: (response) => {
			if (state.requestId !== requestId) {
				return;
			}
			const garage = response?.garage ?? [];
			state.garage = validateGaragePayload(garage) ? garage : [];
			state.loaded = true;
			state.loading = false;
			state.lastGarageFetchOk = true;

			if (response && Object.keys(response).length > 0) {
				localStorageSlim.set(STORAGE_KEYS.GARAGE_V1, state.garage, { ttl: rp_app.cacheConfig.getTtlFor('garage') });
			}

			triggerGarageUpdated();
		},
		error: () => {
			if (state.requestId !== requestId) {
				return;
			}
			state.garage = [];
			state.loaded = true;
			state.loading = false;
			state.lastGarageFetchOk = false;

			// Avoid notifying STEER with an empty garage on a failed GET — that would
			// clear STEER's vehicle. Callers that just changed selection should check
			// invalidateAndRefetchGarage().ok and reload if the refetch failed.
			triggerGarageUpdated({ notifySteer: false });
		},
	});
}

function clearGarageCache() {
	localStorageSlim.remove(STORAGE_KEYS.GARAGE_V1);
	const state = getGarageState();
	state.garage = null;
	state.loaded = false;
	state.loading = false;
}

function getGarageData(isVehicleSpecificPage = false) {
	const state = getGarageState();

	if (state.loading) {
		return null;
	}

	if (state.garage) {
		return state.garage;
	}

	if (!state.loaded && !state.loading) {
		if (loadGarageFromCache(isVehicleSpecificPage)) {
			return state.garage;
		}

		loadUserGarage();
	}
	return null;
}

/**
 * Promise wrapper around the existing event-driven load. Resolves with
 * the garage list once available, or `null` if the load can't happen
 * (e.g. `rp$` unavailable). Lets consumers piggyback on the vehicle picker's
 * existing fetch.
 *
 * @returns {Promise<Array|null>}
 */
function whenGarageReady() {
	const state = getGarageState();
	if (state.loaded && Array.isArray(state.garage)) {
		return Promise.resolve(state.garage);
	}
	if (typeof document === 'undefined' || typeof rp$ !== 'function') {
		return Promise.resolve(null);
	}

	return new Promise((resolve) => {
		const $doc = rp$(document);
		const handler = (_event, data) => {
			$doc.off('garageUpdated', handler);
			resolve(Array.isArray(data) ? data : (state.garage ?? null));
		};
		$doc.on('garageUpdated', handler);
		// `getGarageData` either fires an AJAX load or hits a sync cache
		// path that also emits `garageUpdated` — handler settles both.
		getGarageData();
	});
}

/**
 * Common envelope returned by `setSelectedVehicle` / `clearSelectedVehicle`.
 * `error` is client-side/network detail; when `null`, callers can check
 * `data.message` for server-side messages. `garage` is the fresh
 * post-refetch list on success, `null` otherwise.
 *
 * @typedef {{ ok: boolean, status: number, data: object|null, garage: Array<object>|null, error: string|null }} GarageWriteResult
 */

/**
 * Form-encoded POST with CSRF returning a flat `GarageWriteResult`.
 *
 * @param {string} url
 * @param {string|undefined} body URL-encoded form body, or undefined.
 * @returns {Promise<GarageWriteResult>}
 */
function postFormWithCsrf(url, body) {
	if (typeof fetch !== 'function') {
		return Promise.resolve({ ok: false, status: 0, data: null, garage: null, error: 'fetch is not available' });
	}

	const init = {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Accept': 'application/json',
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	};
	if (body !== undefined) {
		init.body = body;
	}

	return csrf.fetchWithCsrf(url, init).then(function (response) {
		return response.json().then(
			function (data) { return { ok: response.ok, status: response.status, data: data, garage: null, error: null }; },
			function () { return { ok: response.ok, status: response.status, data: null, garage: null, error: null }; },
		);
	}, function (err) {
		return { ok: false, status: 0, data: null, garage: null, error: err?.message || 'request failed' };
	});
}

/**
 * Coerces a YMM field to a string. Strings pass through; finite numbers
 * stringify; everything else (null, undefined, NaN, objects) collapses
 * to '', which callers treat as the "missing" sentinel.
 *
 * @param {unknown} value
 * @returns {string}
 */
function coerceYmmField(value) {
	if (typeof value === 'string') {
		return value;
	}
	if (typeof value === 'number' && Number.isFinite(value)) {
		return String(value);
	}
	return '';
}

/**
 * Sets the selected vehicle via the vehicle-picker endpoint. Year/make/model
 * are required (slug form, year also accepts a finite number); trim and
 * engine optional. Stores without `hasOnlyYmmPartSources` reject incomplete
 * picks server-side. On success, clears the local cache and refetches so
 * the result's `garage` reflects the new selection. Never redirects.
 *
 * @param {{ year: string|number, make: string, model: string, trim?: string, engine?: string }} vehicle
 * @returns {Promise<GarageWriteResult>}
 */
function setSelectedVehicle(vehicle) {
	const year = coerceYmmField(vehicle?.year);
	const make = coerceYmmField(vehicle?.make);
	const model = coerceYmmField(vehicle?.model);
	const trim = coerceYmmField(vehicle?.trim);
	const engine = coerceYmmField(vehicle?.engine);

	if (year === '' || make === '' || model === '') {
		return Promise.resolve({ ok: false, status: 0, data: null, garage: null, error: 'invalid arguments' });
	}

	const body = new URLSearchParams({
		year: year,
		make: make,
		model: model,
		trim: trim,
		engine: engine,
	}).toString();

	return postFormWithCsrf(SELECT_VEHICLE_ENDPOINT, body).then(function (result) {
		if (!result.ok) {
			return result;
		}
		return invalidateAndRefetchGarage().then(function (refetch) {
			return { ...result, garage: refetch.garage };
		});
	});
}

/**
 * Drops the cached garage and resolves with the fresh list. Lets writes
 * resolve "when the data is stable" rather than just on POST completion.
 * Concurrent loads coalesce via `loadUserGarage`'s `state.loading` guard.
 * Never rejects. `ok` is false when the get-garage AJAX errors (STEER was not notified).
 *
 * @returns {Promise<{ garage: Array<object>|null, ok: boolean }>}
 */
function invalidateAndRefetchGarage() {
	clearGarageCache();
	// Bump between the clear and the new fetch so any in-flight
	// `loadUserGarage` callback sees the mismatch and bails — guards
	// against a stale response clobbering the write-path.
	const state = getGarageState();
	state.requestId += 1;
	state.lastGarageFetchOk = undefined;
	return whenGarageReady().then((garage) => ({
		garage,
		ok: state.lastGarageFetchOk === true,
	}));
}

/**
 * Clears the selected vehicle via the search-all-cars endpoint. Garage
 * entries themselves are preserved — important for DMS-mirrored garages
 * like Stellantis/AutoNation. Clears the local cache on success.
 *
 * @returns {Promise<GarageWriteResult>}
 */
function clearSelectedVehicle() {
	return postFormWithCsrf(SEARCH_ALL_CARS_ENDPOINT, undefined).then(function (result) {
		if (!result.ok) {
			return result;
		}
		return invalidateAndRefetchGarage().then(function (refetch) {
			return { ...result, garage: refetch.garage };
		});
	});
}

const handleGarageData = {
	clearGarageCache,
	getGarageData,
	invalidateAndRefetchGarage,
	whenGarageReady,
};

module.exports = {
	clearSelectedVehicle,
	handleGarageData,
	setSelectedVehicle,
};
